import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import SizeChartModal from './SizeChartModal';

interface Option {
  value: string;
  label: string;
  tooltip?: string; // Добавляем необязательное свойство для всплывающей подсказки
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (selected: string[]) => void;
  name?: string;
  label?: string;
  required?: boolean;
  showTooltipIcon?: boolean; // Новое свойство для отображения иконки подсказки
}

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChartButton = styled.button`
  background-color: rgba(36, 186, 125, 0.1);
  color: #24BA7D;
  border: 1px solid rgba(36, 186, 125, 0.3);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: var(--font-family-main);
  
  &:hover {
    background-color: rgba(36, 186, 125, 0.2);
  }
`;

const Label = styled.label`
  font-family: var(--font-family-main);
  font-weight: 400;
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
`;

const InfoIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text-secondary);
  font-size: 10px;
  font-weight: bold;
  cursor: help;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #1A2E2E;
  color: #FFFFFF;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1001;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;

  ${InfoIcon}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;


const DropdownButton = styled.button<{ $isOpen: boolean; $hasValue: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => (props.$isOpen ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.12)')};
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.04);
  color: ${props => (props.$hasValue ? 'var(--color-text)' : 'rgba(255, 255, 255, 0.4)')};
  font-family: var(--font-family-main);
  font-size: 16px;
  line-height: 1.5;
  min-height: 48px;
  transition: all 0.2s ease;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover:not(:disabled) {
    border-color: ${props => (props.$isOpen ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.24)')};
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownArrow = styled.div<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${props => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg {
    width: 100%;
    height: 100%;
    stroke: currentColor;
  }
`;

const DropdownList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #0e1f1f;
  border: 1px solid var(--color-primary);
  border-radius: 4px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(-8px)')};
  transition: all 0.2s ease;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 3px;
    border: 1px solid #0e1f1f;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #1e9e6b;
  }

  scrollbar-width: thin;
  scrollbar-color: var(--color-primary) transparent;
`;

const DropdownOption = styled.div<{ $isSelected: boolean; $isHighlighted: boolean }>`
  padding: 12px 16px;
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props =>
    props.$isSelected ? 'var(--color-primary)' : props.$isHighlighted ? '#172A2A' : 'transparent'};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${props => (props.$isSelected ? 'var(--color-primary)' : '#172A2A')};
  }
`;

const Checkbox = styled.div<{ $isSelected: boolean }>`
  width: 16px;
  height: 16px;
  border: 1px solid var(--color-primary);
  border-radius: 4px;
  background-color: ${props => (props.$isSelected ? 'var(--color-primary)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;
    stroke: #0e1f1f;
    visibility: ${props => (props.$isSelected ? 'visible' : 'hidden')};
  }
`;

// Карта размеров
const sizeMap: { [key: string]: [number, number, number] } = {
  "3.5 US":  [3.5, 3, 35.5],
  "4 US":    [4, 3.5, 36],
  "4.5 US":  [4.5, 4, 36.5],
  "5 US":    [5, 4.5, 37.5],
  "5.5 US":  [5.5, 5, 38],
  "6 US":    [6, 5.5, 38.5],
  "6.5 US":  [6.5, 6, 39],
  "7 US":    [7, 6.5, 40],
  "7.5 US":  [7.5, 7, 40.5],
  "8 US":    [8, 7.5, 41],
  "8.5 US":  [8.5, 8, 42],
  "9 US":    [9, 8.5, 42.5],
  "9.5 US":  [9.5, 9, 43],
  "10 US":   [10, 9.5, 44],
  "10.5 US": [10.5, 10, 44.5],
  "11 US":   [11, 10.5, 45],
  "11.5 US": [11.5, 11, 45.5],
  "12 US":   [12, 11.5, 46],
  "12.5 US": [12.5, 12, 47],
  "13 US":   [13, 12.5, 47.5],
  "13.5 US": [13.5, 13, 48],
  "14 US":   [14, 13.5, 48.5],
  "14.5 US": [14.5, 14, 49],
  "15 US":   [15, 14.5, 49.5],
  "15.5 US": [15.5, 15, 50],
  "16 US":   [16, 15.5, 50.5],
  "16.5 US": [16.5, 16, 51],
  "17 US":   [17, 16.5, 51.5],
  "17.5 US": [17.5, 17, 52],
  "18 US":   [18, 17.5, 52.5],
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  placeholder = 'Выберите опции',
  disabled = false,
  onChange,
  name,
  label,
  required = false,
  showTooltipIcon = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    const newSelected = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newSelected);
  };

  const getButtonLabel = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      const selectedOption = options.find(o => o.value === selectedValues[0]);
      return selectedOption ? selectedOption.label : placeholder;
    }
    return `Выбрано: ${selectedValues.length}`;
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      {label && (
        <LabelContainer>
          <Label>{label}{required && ' *'}</Label>
          {showTooltipIcon && (
            <InfoIcon>
              i
              <Tooltip>
                US - Американский размер
                <br />
                UK - Британский размер
                <br />
                EU - Европейский размер
              </Tooltip>
            </InfoIcon>
          )}
          {label.toLowerCase().includes('размер') && (
            <ChartButton onClick={(e) => {
              e.preventDefault();
              setIsSizeChartOpen(true);
            }}>
              Таблица размеров
            </ChartButton>
          )}
        </LabelContainer>
      )}
      <DropdownButton
        type="button"
        $isOpen={isOpen}
        $hasValue={selectedValues.length > 0}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span>{getButtonLabel()}</span>
        <DropdownArrow $isOpen={isOpen}>
          <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </DropdownArrow>
      </DropdownButton>
      <DropdownList $isOpen={isOpen}>
        {options.map((option, index) => (
          <DropdownOption
            key={option.value}
            $isSelected={selectedValues.includes(option.value)}
            $isHighlighted={index === highlightedIndex}
            onClick={() => handleSelect(option.value)}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <Checkbox $isSelected={selectedValues.includes(option.value)}>
              <svg fill="none" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </Checkbox>
            {option.label}
            {option.tooltip && (
              <InfoIcon
                onMouseEnter={() => setTooltipVisible(option.value)}
                onMouseLeave={() => setTooltipVisible(null)}
              >
                i
                {tooltipVisible === option.value && <Tooltip>{option.tooltip}</Tooltip>}
              </InfoIcon>
            )}
          </DropdownOption>
        ))}
      </DropdownList>
      {/* Модальное окно с таблицей размеров */}
      <SizeChartModal
        isOpen={isSizeChartOpen}
        onClose={() => setIsSizeChartOpen(false)}
        sizeMap={sizeMap}
      />
    </DropdownContainer>
  );
};

export default MultiSelectDropdown;